import React, { useEffect, useState, useRef } from 'react';
import { Card, CardContent } from '../../ui/card';
import { Input } from '../../ui/input';
import { Slider } from '../../ui/slider';
import { Plus, Trash2, ChevronUp, List, Sparkles, Link as LinkIcon, Save, User, Calendar, ThumbsDown, Meh, ThumbsUp, Star, Rocket, Paperclip, Image } from 'lucide-react';
import { useSupabase } from '../../../contexts/SupabaseContext';
import type { Idea, IdeaFeature, FeatureType } from '../../../types/database';

interface IdeaCardExpandedProps {
  idea: Idea;
  onCollapse: () => void;
  promote: () => void;
  deleteIdea: () => void;
}

const IdeaCardExpanded: React.FC<IdeaCardExpandedProps> = ({ idea, onCollapse, promote, deleteIdea }) => {
  const {
    updateIdea,
    loadIdeaFeatures,
    ideaFeatures,
    createIdeaFeature,
    updateIdeaFeature,
    deleteIdeaFeature,
  } = useSupabase();

  const [title, setTitle] = useState(idea.title);
  const [description, setDescription] = useState(idea.description || '');
  const [rank, setRank] = useState(idea.rank ?? 5);
  const [assignee, setAssignee] = useState('');
  const titleRef = useRef<HTMLInputElement>(null);

  // load features once
  useEffect(() => {
    if (!ideaFeatures[idea.id]) {
      loadIdeaFeatures(idea.id);
    }
  }, [idea.id, ideaFeatures, loadIdeaFeatures]);

  // focus title when idea is new
  useEffect(() => {
    if (idea.title === '') {
      titleRef.current?.focus();
    }
  }, [idea.title]);

  // save title on blur
  const handleTitleBlur = () => {
    if (title.trim() && title !== idea.title) {
      updateIdea(idea.id, { title: title.trim() });
    }
  };

  const handleDescBlur = () => {
    if (description !== (idea.description || '')) {
      updateIdea(idea.id, { description: description.trim() });
    }
  };

  const handleRankChange = (value: number) => {
    setRank(value);
    if (value !== idea.rank) {
      updateIdea(idea.id, { rank: value });
    }
  };

  const rows = ideaFeatures[idea.id] || [];
  const filterRows = (type: FeatureType) => rows.filter((f) => f.type === type);

  // add new row helper
  const addRow = async (type: FeatureType, titleValue: string, linkValue?: string) => {
    if (!titleValue.trim()) return;
    await createIdeaFeature({
      idea_id: idea.id,
      title: titleValue.trim(),
      link: linkValue || null,
      sort_order: rows.length + 1,
      type,
    } as IdeaFeature);
  };

  // Nested components to comply with hooks rules
  const SimpleRows: React.FC<{ type: FeatureType; label: string }> = ({ type, label }) => {
    const list = filterRows(type);
    const [newVal, setNewVal] = useState('');
    const newInputRef = useRef<HTMLInputElement>(null);

    return (
      <div className="space-y-1">
        <h4 className="font-medium text-sm flex items-center gap-1">
          {label === 'Features' && <List size={14} />}
          {label === 'Brainstorm' && <Sparkles size={14} />}
          {label !== 'Features' && label !== 'Brainstorm' && <LinkIcon size={14} />}
          {label}
        </h4>
        {list.map((f) => (
          <div key={f.id} className="flex items-center gap-2">
            <Input
              className="flex-1"
              defaultValue={f.title}
              onBlur={(e) => {
                const val = e.target.value;
                if (val.trim() && val !== f.title) {
                  updateIdeaFeature(f.id, { title: val.trim() });
                }
              }}
            />
            <button
              className="text-muted-foreground hover:text-destructive"
              onClick={() => deleteIdeaFeature(f.id)}
            >
              <Trash2 size={16} />
            </button>
          </div>
        ))}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            addRow(type, newVal);
            setNewVal('');
            newInputRef.current?.focus();
          }}
          className="flex items-center gap-2"
        >
          <Plus size={16} />
          <Input
            placeholder={`Add ${label.toLowerCase().slice(0, -1)}…`}
            value={newVal}
            onChange={(e) => setNewVal(e.target.value)}
            ref={newInputRef}
          />
        </form>
      </div>
    );
  };

  const WebLinks: React.FC = () => {
    const list = filterRows('weblink');
    const [newUrl, setNewUrl] = useState('');
    const [newNote, setNewNote] = useState('');
    const urlRef = useRef<HTMLInputElement>(null);

    return (
      <div className="space-y-1">
        <h4 className="font-medium text-sm flex items-center gap-1"><LinkIcon size={14}/> Web Links</h4>
        {list.map((f) => (
          <div key={f.id} className="flex items-center gap-2">
            <Input
              className="flex-1"
              defaultValue={f.title}
              placeholder="https://example.com"
              onBlur={(e) => {
                const val = e.target.value;
                if (val.trim() && val !== f.title) {
                  updateIdeaFeature(f.id, { title: val.trim() });
                }
              }}
            />
            <Input
              className="flex-1"
              defaultValue={f.link || ''}
              placeholder="Notes…"
              onBlur={(e) => {
                const val = e.target.value;
                if (val.trim() !== (f.link || '')) {
                  updateIdeaFeature(f.id, { link: val.trim() });
                }
              }}
            />
            <button
              className="text-muted-foreground hover:text-destructive"
              onClick={() => deleteIdeaFeature(f.id)}
            >
              <Trash2 size={16} />
            </button>
          </div>
        ))}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!newUrl.trim()) return;
            addRow('weblink', newUrl.trim(), newNote.trim());
            setNewUrl('');
            setNewNote('');
            urlRef.current?.focus();
          }}
          className="flex items-center gap-2"
        >
          <Plus size={16} />
          <Input
            placeholder="https://example.com"
            value={newUrl}
            onChange={(e) => setNewUrl(e.target.value)}
            className="flex-1"
            ref={urlRef}
          />
          <Input
            placeholder="Notes…"
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            className="flex-1"
          />
        </form>
      </div>
    );
  };

  const ratingMeta = [
    {word:'Terrible', icon: (c:string)=><ThumbsDown size={14} className={c}/>},
    {word:'Weak', icon: (c:string)=><ThumbsDown size={14} className={c}/>},
    {word:'Meh', icon: (c:string)=><Meh size={14} className={c}/>},
    {word:'Okay-ish', icon: (c:string)=><Meh size={14} className={c}/>},
    {word:'Decent', icon: (c:string)=><ThumbsUp size={14} className={c}/>},
    {word:'Solid', icon: (c:string)=><ThumbsUp size={14} className={c}/>},
    {word:'Strong', icon: (c:string)=><Star size={14} className={c}/>},
    {word:'Great', icon: (c:string)=><Star size={14} className={c}/>},
    {word:'Brilliant', icon: (c:string)=><Rocket size={14} className={c}/>},
    {word:'Epic', icon: (c:string)=><Rocket size={14} className={c}/>},
  ];

  const rankWord = (val:number)=> ratingMeta[val-1]?.word || '';
  const rankIconElem = (val:number)=> {
    const c = val<=3? 'text-destructive': val<=6? 'text-foreground': val<=8? 'text-primary': val===9? 'text-green-600':'text-yellow-500';
    const renderer = ratingMeta[val-1]?.icon;
    return renderer? renderer(c): null;
  };

  const handleSave = async () => {
    if (title.trim() === '') {
      titleRef.current?.focus();
      return;
    }
    handleTitleBlur();
    handleDescBlur();
    // future: persist assignee
    onCollapse();
  };

  return (
    <Card className="col-span-full border-2 border-primary/40">
      <CardContent className="p-4 space-y-4">
        {/* header */}
        <div className="flex items-start justify-between">
          <div className="space-y-2 flex-1">
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              {/* title input follows */}
            </div>
            <Input
              className="text-lg font-semibold placeholder:text-muted-foreground/60"
              value={title}
              placeholder="Idea"
              onChange={(e) => setTitle(e.target.value)}
              onBlur={handleTitleBlur}
              ref={titleRef}
            />
            <textarea
              className="w-full border rounded-md p-2 text-sm"
              value={description}
              placeholder="Description…"
              onChange={(e) => setDescription(e.target.value)}
              onBlur={handleDescBlur}
              rows={6}
            />
          </div>
          <div className="flex items-center gap-3">
            <button onClick={onCollapse} className="text-muted-foreground hover:text-primary">
              <ChevronUp size={20} />
            </button>
          </div>
        </div>

        {/* rank */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 text-xs min-w-[110px]">
            {rankIconElem(rank)}
            <span>{rankWord(rank)}</span>
          </div>
          <div className="flex-1 max-w-sm">
            <Slider
              defaultValue={[rank]}
              min={1}
              max={10}
              step={1}
              onValueChange={(val)=>setRank(val[0] as number)}
              onValueCommit={(val) => handleRankChange(val[0] as number)}
            />
          </div>
        </div>

        {/* feature lists */}
        <SimpleRows type="feature" label="Features" />
        <SimpleRows type="brainstorm" label="Brainstorm" />
        <WebLinks />

        <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <User size={12}/> <Input className="h-6 w-32" placeholder="Assign..." value={assignee} onChange={e=>setAssignee(e.target.value)}/>
          </div>
          <div className="flex items-center gap-1">
            <Calendar size={12}/> {new Date(idea.created_at).toLocaleDateString()}
          </div>
        </div>

        <div className="flex gap-3 text-muted-foreground text-xs">
          <button className="inline-flex items-center gap-1"><Paperclip size={14}/> Attach File</button>
          <button className="inline-flex items-center gap-1"><Image size={14}/> Photo</button>
        </div>

        <div className="flex gap-2 mt-4">
          <button
            onClick={handleSave}
            className="inline-flex items-center gap-1 px-3 py-2 bg-primary text-white rounded"
          >
            <Save size={16} /> Save
          </button>
          <button
            onClick={deleteIdea}
            className="inline-flex items-center gap-1 px-3 py-2 bg-destructive text-white rounded"
          >
            <Trash2 size={16} /> Delete
          </button>
          <button onClick={promote} className="inline-flex items-center gap-1 px-3 py-2 bg-secondary text-primary rounded">
            Convert to Project
          </button>
        </div>
      </CardContent>
    </Card>
  );
};

export default IdeaCardExpanded; 